import "./RedeemVenueList.scss"

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Typography, Collapse, Button, List, Space } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../loading-spinner/loading-spinner";
import { useTranslation } from "react-i18next";
import useLocationsWithVenues, { CountryEntry, StateEntry, VenueEntry } from "../../hooks/useLocationsWithVenues";
import { GoogleTagManagerEvents } from "../../helpers/googleTagManager";
import { ICurrency } from '../../interfaces/ICurrency';
import { IAnalyticsAction, IApplicationState } from "../../store";

const { Title, Text } = Typography;

interface IRedeemVenueListProps {
    parentContainerId: string;
    currencyFilter: ICurrency | undefined;
    venueOnClick?: (venueId: number) => void | Promise<void>;
}

function safeLocaleCompare(
    a: string | null | undefined,
    b: string | null | undefined,
    locales?: string | string[],
    options?: Intl.CollatorOptions
): number {
    const sa = a ?? "";
    const sb = b ?? "";
    return sa.localeCompare(sb, locales, options);
}

// this component is similar to the AllVenuesList used in the location picker widget
// important difference is that it doesn't split between a tab for region-country and another tab for state-venue
// It instead displays countries as an accordian containing states, each state is also an accordian that contains venues

const RedeemVenueList: React.FC<IRedeemVenueListProps> = ({ parentContainerId, currencyFilter, venueOnClick }) => {
    const { t } = useTranslation();
    const bookHereLabel = t('globalGiftVouchers.bookHere');

    const dispatch = useDispatch();

    const sitesLoading = useSelector((s: IApplicationState) => s.sites.sitesLoading);
    const sitesLoaded = useSelector((s: IApplicationState) => s.sites.sitesLoaded);

    //filter all venues to only those using the selected currency
    const filterForRegionalGiftVouchers = true;
    const { nestedMap: nestedLocationsMap } = useLocationsWithVenues(currencyFilter, filterForRegionalGiftVouchers);

    const allCountries = useMemo(() => {
        const result: Record<string, CountryEntry> = {};
        if (!nestedLocationsMap) return result;

        for (const region of Object.values(nestedLocationsMap)) {
            Object.assign(result, region.countries);
        }

        return result;
    }, [nestedLocationsMap]);

   // Utility function to scroll a collapse header to the top of the widget
    const scrollHeaderToTop = useCallback((panelKey: string | string[]) => {

        console.log(panelKey);

        // Wait for collapse animation to complete
        setTimeout(() => {
            const key = Array.isArray(panelKey) ? panelKey[0] : panelKey;
            if (!key) return;

            const container = document.querySelector(`#${parentContainerId}`) as HTMLElement | null;
            const header = document.getElementById(`panel-header-${key}`);

            console.log(container);
            console.log(header);

            if (container && header) {
                const containerRect = container.getBoundingClientRect();
                const headerRect = header.getBoundingClientRect();
                const target = container.scrollTop + (headerRect.top - containerRect.top);

                container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
            }
            else {
                console.log(`couldn't find container or header`);
            }

        }, 200); // Wait for Ant Design collapse animation
    }, [parentContainerId]);

    // starts as null, which causes auto expand on a single country, set to undefined when the user minamises the country header manually
    const [selectedCountry, setSelectedCountry] = useState<CountryEntry | null | undefined>(null);

    useEffect(() => {
        //use effect to auto select the country when only one is available / relavent and the component is first initialised
      const keys = Object.keys(allCountries);
      if (keys.length === 1) {
        const onlyCountry = allCountries[keys[0]];
          if (onlyCountry && selectedCountry?.countryData.key !== onlyCountry.countryData.key && selectedCountry === null) {
          setSelectedCountry(onlyCountry);
        }
    }
    }, [allCountries, selectedCountry]);

    const onCountryClicked = (panelKey: string | string[]) => {
        //grab the first state key
        const countryKey = Array.isArray(panelKey) ? panelKey[0] : panelKey;

        const clickedCountry = allCountries?.[countryKey];

        //remember the selected country
        setSelectedCountry(clickedCountry);

        scrollHeaderToTop(panelKey);

        dispatch({
            type: 'ANALYTICS_EVENT',
            googleTagManagerEvent: GoogleTagManagerEvents.LocationWidgetClick_country(clickedCountry?.countryData?.key ?? 'none', clickedCountry?.countryData?.value ?? 'none'),
        } as IAnalyticsAction);
    };

    const onStateClicked = (panelKey: string | string[]) => {
        //grab the first state key
        const stateKey = Array.isArray(panelKey) ? panelKey[0] : panelKey;
        //check we can find the states data in our selected country
        if (!selectedCountry) {
            console.warn(`user clicked state with key '${stateKey}', but a country was not currently selected`);
            return;
        }
        //remember the selected state
        const clickedState = selectedCountry.states[stateKey];
        //setSelectedState(selectedCountry.states[stateKey]);

        //ensure clicked header is scrolled into view
        scrollHeaderToTop(panelKey);

        dispatch({
            type: 'ANALYTICS_EVENT',
            googleTagManagerEvent: GoogleTagManagerEvents.LocationWidgetClick_state(clickedState?.stateData?.key ?? 'none', clickedState?.stateData?.value ?? 'none'),
        } as IAnalyticsAction);
    };


    // =================================================================================
    // Ui fragments that we return 

    if (sitesLoading) return (
        <div style={{ marginBottom: '20px' }} >
            <LoadingSpinner />
        </div>
    );

    if (!sitesLoading && (!sitesLoaded || !nestedLocationsMap)) {
        return (
            <WarningOutlined style={{ color: "red", fontSize: 32, width: "100%" }} />
        );
    }

    //define how each clickable venue should appear
    const renderVenueListItem = (venue: VenueEntry) => {
        // view only list item for global gift voucher purchase path
        if (venueOnClick === undefined) {
            return (
                <List.Item key={venue.id}>
                    <div className="venue-btn">
                        <Text strong>{venue.value?.toUpperCase()}</Text>
                    </div>
                </List.Item>
            );
        }
        else {
            //clickable list item for redeemable locations page to drive users from gift voucher email to booking purchase path
            return (
                <List.Item key={venue.id}>

                    <Button
                        type="link"
                        block
                        className="venue-btn"
                        onClick={() => venueOnClick?.(venue.id)}
                    >
                        <div className="venue-btn">
                            <Text strong>{venue.value?.toUpperCase()}</Text>
                            <div className="venue-details">
                                <div className="venue-address">
                                    <Typography.Paragraph>
                                        {venue.address1 && <>{venue.address1}<br /></>}
                                        {venue.address2 && <>{venue.address2}<br /></>}
                                        {venue.address3 && <>{venue.address3}<br /></>}
                                    </Typography.Paragraph>
                                </div>

                                <div className="venue-distance" >
                                    <Typography.Text>{bookHereLabel}</Typography.Text>
                                </div>
                            </div>
                        </div>
                    </Button>
                    
                </List.Item>
            );
        }
       
    };

    // define how each state/provence should be rendered
    const renderStateContent = (state: StateEntry) => {
        const serviceAreas = Object.values(state.serviceAreas).sort((a, b) =>
            safeLocaleCompare(a.areaData.value, b.areaData.value, undefined, { numeric: true, sensitivity: "base" })
        );
        const showAreaTitle = serviceAreas.length > 1;

        return (
            <div className="VenuesInStateList">
                {serviceAreas.map((area) => (
                    <div key={area.areaData.key}>
                        {showAreaTitle && (
                            <Title level={3} style={{ fontWeight: "bold" }}>
                                {area.areaData.value?.toUpperCase()}
                            </Title>
                        )}
                        <List
                            className={`venueItems ${showAreaTitle ? 'with-state-heading': ''}` }
                            dataSource={[...area.venues].sort((a, b) =>
                                safeLocaleCompare(a.value, b.value, undefined, { numeric: true, sensitivity: "base" })
                            )}
                            renderItem={(v) => renderVenueListItem(v)}
                        />
                    </div>
                ))}
            </div>
        );
    };

    //render the content for a selected country (eg list of available states and venues)
    const renderCountryContent = (country: CountryEntry) => {
        //if we havent selected a country, we can't build  list for its states and venues
        if (!country) return (<></>);

        //build a list of each state in the country
        const items =
            Object.values(country.states).sort((a, b) =>
                safeLocaleCompare(a.stateData.value, b.stateData.value, undefined, { numeric: true, sensitivity: "base" })
            )
            .map((state) => ({
                key: state.stateData.key,
                header: state.stateData.value?.toUpperCase(),
                content: renderStateContent(state),
            }));


        //if there are multiple states in the country use a collapse list, one section per state
        if (items.length > 1) {
            return (
                <Collapse accordion onChange={onStateClicked}>
                    {items.map((it) => (
                        <Collapse.Panel header={it.header} key={it.key} id={`panel-header-${it.key}`}>
                            {it.content}
                        </Collapse.Panel>
                    ))}
                </Collapse>
            );
        }
        else if (items.length === 1) {
            //if there is only one state just return that state view directly without nesting
            return (items[0].content);
        }


        //else
        return (<div>No venues in country</div>);
    };

    //render the content for the world (eg all the regions and the country options in them)
    const renderWorldContent = () => {
        if (!nestedLocationsMap) return (<>No region data</>);

        //list out each country as a collapse heading
        const items =
            Object.values(allCountries).sort((a, b) =>
                safeLocaleCompare(a.countryData.value, b.countryData.value, undefined, { numeric: true, sensitivity: "base" })
            )
                .map((country) => ({
                    key: country.countryData.key,
                    header: country.countryData.value?.toUpperCase(),
                    content: renderCountryContent(country),
                }));

        const activeKey = selectedCountry ? selectedCountry.countryData.key : undefined;

        //if there are multiple states in the country use a collapse list, one section per state
        return (
            <Collapse accordion onChange={onCountryClicked} activeKey={activeKey}>
                {items.map((it) => (
                    <Collapse.Panel header={it.header} key={it.key} id={`panel-header-${it.key}`} >
                        {it.content}
                    </Collapse.Panel>
                ))}
            </Collapse>
        );

    };


    // Main tabbed view
    return (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div id="redeem-venue-list">
                {renderWorldContent()}
            </div>
        </Space>
    );
};

export default RedeemVenueList;

